import { BACKEND_EVENTS, WebSocketReponse } from "fieldpro-tools";

interface INotificationRecord {
  [messageType: string]: number;
}

interface INotificationConfig {
  /**
   * Time interval in milliseconds that must pass before showing the notification again
   */
  intervalMs: number;
}

type TEnvironment = "production" | "staging" | "develop" | "local" | "test";

const NOTIFICATIONS_STORAGE_KEY = "fp_notifications";

const HOUR_MS = 60 * 60 * 1000;
const MINUTE_MS = 60 * 1000;
const SECOND_MS = 1000;

const getEnvironment = (): TEnvironment => {
  const env = window._env_?.NODE_ENV;
  if (!env || env === "development") {
    return "local";
  }
  return env as TEnvironment;
};

const getNotificationConfig = (_messageType: string): INotificationConfig => {
  const env = getEnvironment();

  // All event types currently use the same time windows
  // Production: 1 hour
  // Staging: 10 minutes
  // Develop: 10 minutes
  // Local/Test: 10 seconds
  switch (env) {
    case "production":
      return { intervalMs: 1 * HOUR_MS };
    case "staging":
      return { intervalMs: 10 * MINUTE_MS };
    case "develop":
      return { intervalMs: 10 * MINUTE_MS };
    case "local":
    case "test":
      return { intervalMs: 40 * SECOND_MS };
    default:
      return { intervalMs: 1 * HOUR_MS };
  }
};

const getNotificationRecord = (): INotificationRecord => {
  try {
    const value = localStorage.getItem(NOTIFICATIONS_STORAGE_KEY);
    if (!value) {
      return {};
    }
    return JSON.parse(value) as INotificationRecord;
  } catch {
    return {};
  }
};

export const updateNotificationRecord = (messageType: string): void => {
  const record = getNotificationRecord();
  record[messageType] = Date.now();
  try {
    localStorage.setItem(NOTIFICATIONS_STORAGE_KEY, JSON.stringify(record));
  } catch (error) {
    console.error("Failed to update notification record:", error);
  }
};

export const shouldShowNotification = (messageType: string): boolean => {
  const record = getNotificationRecord();
  const lastShown = record[messageType];

  if (!lastShown) {
    return true;
  }

  const config = getNotificationConfig(messageType);
  const timeSinceLastShown = Date.now() - lastShown;
  return timeSinceLastShown >= config.intervalMs;
};

export const handleNotificationEvent = (
  messageType: string,
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>,
): void => {
  if (shouldShowNotification(messageType)) {
    setShowDialog(true);
    updateNotificationRecord(messageType);
  }
};

export function isReleaseEvent(
  event: WebSocketReponse<any>,
): event is WebSocketReponse<null> {
  return [
    // BACKEND_EVENTS.NEW_RELEASE_BE, <-- Uncomment if needed
    BACKEND_EVENTS.NEW_RELEASE_FE,
  ].includes(event.type);
}

export function isMigrationOngoingEvent(
  event: WebSocketReponse<any>,
): event is WebSocketReponse<null> {
  return event.type === BACKEND_EVENTS.IS_MIGRATION_ONGOING;
}
